import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'chart.js/auto';
import api from '../../services/api.js';
import './executive-management-dashboard.scss';

const BLUE = { color: '#005293' };
const PAGE_SIZE = 10;
const TIMEFRAMES = [['30d', 'Last 30 Days'], ['7d', 'Last 7 Days'], ['today', 'Today (Live)'], ['ytd', 'Year to Date']];
const CITIES = [['ALL', 'All Metro Regions'], ['Taguig', 'Taguig (BGC)'], ['Makati', 'Makati'], ['Pasig', 'Pasig (Ortigas)'], ['Mandaluyong', 'Mandaluyong'], ['Cebu City', 'Cebu City'], ['Clark', 'Clark Freeport'], ['Quezon City', 'Quezon City']];
const PLUGS = {
  CCS2: { label: 'CCS2 (DC Fast)', color: '#0284c7' },
  Type2: { label: 'Type 2 (AC Destination)', color: '#005293' },
  CHAdeMO: { label: 'CHAdeMO (Legacy DC)', color: '#d97706' },
  GBT: { label: 'GB/T (Chinese Standard)', color: '#7c3aed' },
};
const php = (v) => `₱${Number(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v, d = 0) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const rankClass = (r) => (r === 1 ? 'rank-gold' : r === 2 ? 'rank-silver' : r === 3 ? 'rank-bronze' : 'rank-default');

function Delta({ pct }) {
  if (pct == null) return <span className="text-muted font-weight-bold">n/a</span>;
  const up = pct >= 0;
  return <span className={`${up ? 'text-success' : 'text-danger'} font-weight-bold`}><i className={`fas fa-arrow-${up ? 'up' : 'down'} me-1`} />{up ? '+' : ''}{pct}%</span>;
}

/** Owns one Chart.js instance; rebuilds it whenever the config changes. */
function useChart(canvasRef, config) {
  const chart = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !config) return undefined;
    chart.current?.destroy();
    chart.current = new Chart(canvasRef.current, config);
    return () => { chart.current?.destroy(); chart.current = null; };
  }, [canvasRef, config]);
}

export default function ExecutiveManagementDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [demand, setDemand] = useState([]);
  const [daytype, setDaytype] = useState('all');
  const [stations, setStations] = useState([]);
  const [meta, setMeta] = useState({ total: 0, sitesOnline: 0, sitesTotal: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('ALL');
  // City choices come from the live station list; the fixed list is only a fallback.
  const [cityOptions, setCityOptions] = useState(CITIES);
  useEffect(() => {
    api.request('/public/stations?pageSize=50')
      .then((list) => {
        const names = [...new Set((Array.isArray(list) ? list : []).map((st) => st.city).filter(Boolean))].sort();
        if (names.length) setCityOptions([['ALL', 'All cities'], ...names.map((n) => [n, n])]);
      })
      .catch(() => {});
  }, []);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);

  const flash = (ok, msg) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 5000); };

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadSummary = useCallback(() => api.executiveSummary(range).then((d) => { setData(d); setError(''); }).catch((e) => setError(e.message)).finally(() => setLoading(false)), [range]);
  const loadDemand = useCallback(() => api.executiveDemand(range, daytype).then(setDemand).catch((e) => setError(e.message)), [range, daytype]);
  const loadStations = useCallback(() => api.executiveStations({ range, q: query, city, page, pageSize: PAGE_SIZE })
    .then((r) => { setStations(r.data); setMeta(r.meta); }).catch((e) => setError(e.message)), [range, query, city, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDemand(); }, [loadDemand]);
  useEffect(() => { loadStations(); }, [loadStations]);

  const refresh = async () => {
    setBusy('refresh');
    await Promise.all([loadSummary(), loadDemand(), loadStations()]);
    setBusy('');
    flash(true, 'Dashboard refreshed with the latest network data.');
  };

  const download = async (siteId, siteName) => {
    setBusy(siteId || 'export');
    try {
      const blob = await api.exportCdrs({ range, siteId });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = siteId ? `cdr-summary-${siteName.replace(/[^A-Za-z0-9]+/g, '-')}.csv` : 'cdr-summary.csv';
      a.click();
      URL.revokeObjectURL(url);
      flash(true, siteId ? `CDR summary for ${siteName} exported to CSV.` : 'Audited CDR summary exported to CSV.');
    } catch (e) {
      flash(false, e.message);
    } finally {
      setBusy('');
    }
  };

  const revRef = useRef(null);
  const energyRef = useRef(null);
  const demandRef = useRef(null);
  const t = data?.revenueTrend;
  const revCfg = t && {
    type: 'bar',
    data: { labels: t.labels, datasets: [
      { label: 'Energy Sales (kWh)', data: t.energy, backgroundColor: '#005293', borderRadius: 4 },
      { label: 'Session Fees', data: t.sessionFees, backgroundColor: '#fecc00', borderRadius: 4 },
      { label: 'Idle & parking fees', data: t.idle, backgroundColor: '#dc2626', borderRadius: 4 },
      { label: 'Reservation Holds', data: t.reservations, backgroundColor: '#0284c7', borderRadius: 4 },
    ] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${php(c.raw)}` } } },
      scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, ticks: { callback: (v) => `₱${v >= 1000 ? `${v / 1000}k` : v}` } } } },
  };
  const plugs = data?.energyByConnector;
  const energyCfg = plugs && {
    type: 'doughnut',
    data: { labels: plugs.map((p) => PLUGS[p.type].label), datasets: [{ data: plugs.map((p) => p.mwh), backgroundColor: plugs.map((p) => PLUGS[p.type].color), borderWidth: 2, borderColor: '#ffffff' }] },
    options: { responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.label}: ${c.raw} MWh` } } } },
  };
  const demandCfg = demand.length > 0 && {
    type: 'line',
    data: { labels: demand.map((d) => d.label), datasets: [{ label: 'Coincident Active Power (kW)', data: demand.map((d) => d.avgKw), borderColor: '#005293', backgroundColor: 'rgba(0, 82, 147, 0.12)', fill: true, tension: 0.35, pointBackgroundColor: '#005293', pointRadius: 3, pointHoverRadius: 6 }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `Avg Draw: ${c.raw} kW`, afterLabel: (c) => `Coincident peak: ${demand[c.dataIndex].peakKw} kW · ${demand[c.dataIndex].sessions} concurrent sessions` } } },
      scales: { x: { grid: { display: false } }, y: { ticks: { callback: (v) => `${v} kW` } } } },
  };
  useChart(revRef, revCfg || null);
  useChart(energyRef, energyCfg || null);
  useChart(demandRef, demandCfg || null);

  const k = data?.kpis;
  const b = data?.breakdown;
  const pages = Math.max(1, Math.ceil(meta.total / PAGE_SIZE));
  const start = meta.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, meta.total);
  const onlinePct = meta.sitesTotal ? Math.round((meta.sitesOnline / meta.sitesTotal) * 100) : 0;

  const streams = b && [
    ['Energy Consumption Sales (kWh)', b.energy, b.energyPct, 'bg-primary', null],
    ['Session Initiation Fees (Fixed)', b.sessionFees, b.sessionFeesPct, '', '#fecc00'],
    ['Idle / Bay Overstay Penalties', b.idle, b.idlePct, 'bg-danger', null],
    ['Advance Connector Reservations (15/30m)', b.reservations, b.reservationsPct, 'bg-info', null],
  ];

  return (
    <div className="container-fluid py-4" id="exec-dashboard-container">
      {toast && <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="exec-toast">{toast.msg}</div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h4 className="font-weight-bold fw-bold mb-0" style={BLUE}><i className="fas fa-chart-line me-2" style={BLUE} />Executive Management Dashboard</h4>
            <span className="badge bg-warning text-dark ms-2 font-weight-bold">Commercial &amp; Energy Telemetry</span>
          </div>
          <p className="text-muted small mb-0">High-level commercial performance, network utilization, multi-component revenue recognition, and load profile analytics across all charging hubs.</p>
        </div>
        <div className="mt-2 mt-md-0 d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group btn-group-sm me-2" role="group" aria-label="Timeframe filter">
            {TIMEFRAMES.map(([key, label]) => (
              <button key={key} type="button" className={`btn btn-outline-primary time-filter-btn ${range === key ? 'active' : ''}`} data-timeframe={key} aria-pressed={range === key} onClick={() => { setRange(key); setPage(1); }}>{label}</button>
            ))}
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary me-2" id="btn-export-exec-report" onClick={() => window.print()}><i className="fas fa-file-pdf me-1" /> Executive Brief</button>
          <button type="button" className="btn btn-sm btn-primary" id="btn-refresh-dashboard" disabled={busy === 'refresh'} onClick={refresh}>
            {busy === 'refresh' ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-sync-alt me-1" />} Refresh Live
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert" id="exec-error">{error}</div>}

      <div className="row mb-4">
        <div className="col-12 col-sm-6 col-xl-3 mb-3"><div className="card border shadow-sm h-100 bg-white exec-kpi-card exec-kpi-success"><div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div><div className="text-muted small text-uppercase font-weight-bold fw-bold">Gross Charging Revenue</div>
              <h3 className="font-weight-bold fw-bold text-success mb-0 mt-1" id="stat-gross-revenue">{loading ? '…' : php(k?.grossRevenue)}</h3></div>
            <div className="p-2 rounded" style={{ backgroundColor: '#ecfdf5', color: '#16a34a' }}><i className="fas fa-coins fa-lg" /></div>
          </div>
          <div className="mt-2 small text-muted d-flex justify-content-between align-items-center"><Delta pct={k?.grossRevenueDeltaPct} /><span>vs previous period</span></div>
        </div></div></div>
        <div className="col-12 col-sm-6 col-xl-3 mb-3"><div className="card border shadow-sm h-100 bg-white exec-kpi-card exec-kpi-primary"><div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div><div className="text-muted small text-uppercase font-weight-bold fw-bold">Energy Dispensed</div>
              <h3 className="font-weight-bold fw-bold mb-0 mt-1" style={BLUE} id="stat-energy-mwh">{loading ? '…' : `${num(k?.energyMwh, 1)} MWh`}</h3></div>
            <div className="p-2 rounded" style={{ backgroundColor: '#e0f2fe', color: '#005293' }}><i className="fas fa-charging-station fa-lg" /></div>
          </div>
          <div className="mt-2 small text-muted d-flex justify-content-between align-items-center"><Delta pct={k?.energyDeltaPct} /><span>{num(k?.energyKwh)} kWh delivered</span></div>
        </div></div></div>
        <div className="col-12 col-sm-6 col-xl-3 mb-3"><div className="card border shadow-sm h-100 bg-white exec-kpi-card exec-kpi-info"><div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div><div className="text-muted small text-uppercase font-weight-bold fw-bold">Completed Sessions</div>
              <h3 className="font-weight-bold fw-bold mb-0 mt-1" style={{ color: '#0284c7' }} id="stat-session-count">{loading ? '…' : num(k?.completedSessions)}</h3></div>
            <div className="p-2 rounded" style={{ backgroundColor: '#f0f9ff', color: '#0284c7' }}><i className="fas fa-plug fa-lg" /></div>
          </div>
          <div className="mt-2 small text-muted d-flex justify-content-between align-items-center"><span className="font-weight-bold fw-bold text-dark">Avg {num(k?.avgKwhPerSession, 1)} kWh</span><span>{num(k?.avgMinutesPerSession, 1)} min / session</span></div>
        </div></div></div>
        <div className="col-12 col-sm-6 col-xl-3 mb-3"><div className="card border shadow-sm h-100 bg-white exec-kpi-card exec-kpi-warning"><div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div><div className="text-muted small text-uppercase font-weight-bold fw-bold">Peak Bay Occupancy</div>
              <h3 className="font-weight-bold fw-bold mb-0 mt-1" style={{ color: '#d97706' }} id="stat-bay-utilization">{loading ? '…' : `${num(k?.peakBayOccupancyPct, 1)}%`}</h3></div>
            <div className="p-2 rounded" style={{ backgroundColor: '#fffbeb', color: '#d97706' }}><i className="fas fa-tachometer-alt fa-lg" /></div>
          </div>
          <div className="mt-2 small text-muted d-flex justify-content-between align-items-center"><span className="text-warning font-weight-bold fw-bold"><i className="fas fa-bolt me-1" />{num(k?.coincidentPeakKw)} kW</span><span>Coincident peak demand</span></div>
        </div></div></div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-lg-8 mb-3 mb-lg-0"><div className="card border shadow-sm bg-white h-100">
          <div className="card-header chart-card-header d-flex flex-wrap justify-content-between align-items-center py-3">
            <div>
              <h6 className="mb-0 font-weight-bold fw-bold" style={BLUE}><i className="fas fa-chart-area me-2" style={BLUE} />Revenue Growth &amp; Composition Trend</h6>
              <span className="text-muted small">Daily revenue split: Energy (kWh), Session fees, Idle penalties, and Reservation holds</span>
            </div>
            <div className="small font-weight-bold fw-bold mt-2 mt-sm-0">
              <span className="me-3"><i className="fas fa-circle me-1" style={{ color: '#005293' }} />Energy</span>
              <span className="me-3"><i className="fas fa-circle me-1" style={{ color: '#fecc00' }} />Sessions</span>
              <span className="me-3"><i className="fas fa-circle me-1" style={{ color: '#dc2626' }} />Idle Fees</span>
              <span><i className="fas fa-circle me-1" style={{ color: '#0284c7' }} />Reservations</span>
            </div>
          </div>
          <div className="card-body p-3"><div className="chart-container-lg"><canvas id="revenueTrendChart" ref={revRef} role="img" aria-label="Revenue trend by fee component" /></div></div>
        </div></div>
        <div className="col-12 col-lg-4"><div className="card border shadow-sm bg-white h-100">
          <div className="card-header chart-card-header py-3">
            <h6 className="mb-0 font-weight-bold fw-bold" style={BLUE}><i className="fas fa-chart-pie me-2" style={BLUE} />Energy Throughput by Connector Plug</h6>
            <span className="text-muted small">MWh volume delivered by standard</span>
          </div>
          <div className="card-body p-3 d-flex flex-column justify-content-between">
            <div className="chart-container-sm"><canvas id="energyThroughputChart" ref={energyRef} role="img" aria-label="Energy by connector standard" /></div>
            <div className="mt-3 pt-2 border-top small text-muted">
              {(plugs || []).map((p, i, all) => (
                <div key={p.type} className={`d-flex justify-content-between ${i < all.length - 1 ? 'mb-1' : ''}`} data-plug={p.type}>
                  <span><i className="fas fa-square me-1" style={{ color: PLUGS[p.type].color }} />{PLUGS[p.type].label}:</span>
                  <strong className="text-dark font-monospace">{num(p.mwh, 1)} MWh ({p.pct}%)</strong>
                </div>
              ))}
            </div>
          </div>
        </div></div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-lg-7 mb-3 mb-lg-0"><div className="card border shadow-sm bg-white h-100">
          <div className="card-header chart-card-header d-flex justify-content-between align-items-center py-3">
            <div>
              <h6 className="mb-0 font-weight-bold fw-bold" style={BLUE}><i className="fas fa-bolt me-2" style={{ color: '#fecc00' }} />24-Hour Network Load Curve (Hourly Peak kW)</h6>
              <span className="text-muted small">Coincident active power draw across 24 hours of the day</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <select className="form-select form-select-sm" style={{ width: 120 }} id="filter-daytype" aria-label="Weekday or weekend" value={daytype} onChange={(e) => setDaytype(e.target.value)}>
                <option value="all">All days</option><option value="weekday">Weekdays</option><option value="weekend">Weekends</option>
              </select>
              <span className="badge bg-primary font-weight-bold">Grid Coincident Peak</span>
            </div>
          </div>
          <div className="card-body p-3"><div className="chart-container-sm"><canvas id="hourlyPeakDemandChart" ref={demandRef} role="img" aria-label="24 hour network load curve" /></div></div>
        </div></div>
        <div className="col-12 col-lg-5"><div className="card border shadow-sm bg-white h-100">
          <div className="card-header chart-card-header py-3">
            <h6 className="mb-0 font-weight-bold fw-bold" style={BLUE}><i className="fas fa-receipt me-2" style={BLUE} />Commercial Tariff Revenue Breakdown</h6>
            <span className="text-muted small">Itemized revenue streams audited via CDR billing</span>
          </div>
          <div className="card-body p-3">
            {(streams || []).map(([name, amount, pct, cls, color]) => (
              <div className="mb-3" key={name} data-stream={name}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="font-weight-bold fw-bold text-dark small">{name}</span>
                  <span className="font-weight-bold fw-bold font-monospace text-dark">{php(amount)} <span className="text-muted small">({pct}%)</span></span>
                </div>
                <div className="progress progress-thin"><div className={`progress-bar ${cls}`} role="progressbar" style={{ width: `${pct}%`, ...(color ? { backgroundColor: color } : {}) }} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} /></div>
              </div>
            ))}
            <div className="pt-3 border-top mt-4">
              <div className="d-flex justify-content-between align-items-center">
                <div><span className="text-muted small text-uppercase font-weight-bold fw-bold">Net revenue after credits</span>
                  <div className="text-success font-weight-bold fw-bold h5 mb-0" id="stat-margin">{b ? `${b.netMarginPct}% of revenue` : '…'}</div></div>
                <div className="text-end"><span className="text-muted small text-uppercase font-weight-bold fw-bold">VAT Collected (12%)</span>
                  <div className="text-dark font-weight-bold fw-bold h5 mb-0 font-monospace" id="stat-vat">{php(b?.vatCollected)}</div></div>
              </div>
            </div>
          </div>
        </div></div>
      </div>

      <div className="card border shadow-sm bg-white mb-4">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
          <div>
            <h6 className="mb-0 font-weight-bold fw-bold" style={BLUE}><i className="fas fa-trophy me-2" style={{ color: '#fecc00' }} />Station Plaza Performance Leaderboard</h6>
            <span className="text-muted small">Charging sites ranked by revenue, delivered kWh, session throughput, and bay occupancy</span>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0">
            <div className="input-group input-group-sm me-2" style={{ width: 220 }}>
              <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
              <input type="text" className="form-control" id="search-leaderboard" aria-label="Search site or city" placeholder="Search site or city..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="form-select form-select-sm" style={{ width: 160 }} id="filter-leaderboard-city" aria-label="City filter" value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }}>
              {cityOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" className="btn btn-sm btn-outline-secondary ms-2" id="btn-export-cdrs-csv" disabled={busy === 'export'} onClick={() => download()}>
              {busy === 'export' ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-file-csv me-1" />} Export CDRs
            </button>
          </div>
        </div>
        <div className="card-body p-0"><div className="table-responsive">
          <table className="table table-hover mb-0" id="table-leaderboard">
            <thead><tr className="text-muted small text-uppercase">
              <th style={{ width: 60 }}>Rank</th><th>Charging Site Plaza</th><th>City / Metro</th><th>Status</th>
              <th className="text-end">Delivered (kWh)</th><th className="text-end">Subtotal</th><th className="text-end">VAT (12%)</th><th className="text-end">Gross Total (PHP)</th><th className="text-end">Sessions</th><th className="text-end">Actions</th>
            </tr></thead>
            <tbody id="leaderboard-table-body">
              {stations.length === 0 && <tr><td colSpan={10} className="text-center p-4 text-muted">No station plazas match the search or city filter.</td></tr>}
              {stations.map((st) => (
                <tr key={st.siteId} data-site={st.siteName}>
                  <td><div className={`leaderboard-rank ${rankClass(st.rank)}`}>{st.rank}</div></td>
                  <td><div className="font-weight-bold fw-bold text-dark">{st.siteName}</div>
                    <div className="small text-muted font-monospace"><span className="badge badge-success-soft me-1">{st.status === 'operational' ? 'Active Hub' : 'Limited'}</span>{st.utilizationPct}% Bay Occupancy</div></td>
                  <td><span className="badge bg-secondary">{st.city}</span></td>
                  <td><span className={`badge ${st.status === 'operational' ? 'badge-available' : 'badge-warning-soft'}`}>{st.status === 'operational' ? 'Operational' : st.status === 'maintenance' ? 'Maintenance' : 'Inactive'}</span></td>
                  <td className="text-end font-monospace font-weight-bold fw-bold text-dark">{num(st.totalEnergyKwh, 1)}</td>
                  <td className="text-end font-monospace text-muted">{php(st.subtotalAmount)}</td>
                  <td className="text-end font-monospace text-muted">{php(st.taxAmount)}</td>
                  <td className="text-end font-monospace font-weight-bold fw-bold text-success">{php(st.totalAmount)}</td>
                  <td className="text-end font-monospace text-dark">{num(st.sessions)}</td>
                  <td className="text-end"><div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-primary btn-view-site" title="View Site Live Telemetry" aria-label={`View live telemetry for ${st.siteName}`} onClick={() => navigate('/noc-fleet-monitoring-alarms')}><i className="fas fa-desktop" /></button>
                    <button type="button" className="btn btn-outline-secondary btn-export-site-cdr" title="Export CDRs" aria-label={`Export CDRs for ${st.siteName}`} disabled={busy === st.siteId} onClick={() => download(st.siteId, st.siteName)}><i className="fas fa-download" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
        <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
          <span className="text-muted small" id="leaderboard-summary-text">Showing {start}&ndash;{end} of {meta.total} charging plazas</span>
          <div className="d-flex align-items-center gap-2">
            <span className={`badge ${onlinePct === 100 ? 'badge-success-soft' : 'badge-warning-soft'} font-weight-bold fw-bold`}>{onlinePct}% sites operational</span>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" id="leaderboard-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">&lsaquo;</button>
              <button type="button" className="btn btn-outline-secondary" id="leaderboard-next" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page">&rsaquo;</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
